import React, { useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import Layout from '../layout/Layout'
import SellContractForm from '../molecules/SellContractForm'
import {
  FETCH_INITIAL_SELL_CONTRACT_VALUES,
  RESET_SELL_CONTRACT_PDF,
  UPDATE_SELL_CONTRACT
} from 'src/redux/sellContractReducer'
import { RootReducer } from 'src/redux/rootReducer'
import { parseOptionalNumericId, useDocumentCreateParams } from 'src/helpers/addVehicleDocument'
import {
  SellContractFormData,
  sellContractEditValues,
  sellContractRequestValues
} from 'src/helpers/sellContractFormValidation'

interface UrlParams {
  clientId: string
  sellContractId: string
  [key: string]: string | undefined
}

const SellContractUpdate = (): JSX.Element => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { sellContractId } = useParams<UrlParams>()
  const { returnPath } = useDocumentCreateParams()
  const loading = useSelector((state: RootReducer) => state.sellContractReducer.loading)
  const sellContractPdf = useSelector((state: RootReducer) => state.sellContractReducer.sell_contract_pdf)
  const initialFormValues = useSelector((state: RootReducer) => {
    return state.sellContractReducer.initialSellContractValues[sellContractId ?? ''] ?? null
  })

  useEffect(() => {
    return () => {
      dispatch(RESET_SELL_CONTRACT_PDF())
    }
  }, [dispatch])

  useEffect(() => {
    if (typeof sellContractId === 'string' && sellContractId.length > 0) {
      dispatch(FETCH_INITIAL_SELL_CONTRACT_VALUES({ id: sellContractId }))
    }
  }, [dispatch, sellContractId])

  useEffect(() => {
    if (sellContractPdf !== null) {
      window.open(sellContractPdf, '_blank')
      navigate(returnPath)
    }
  }, [sellContractPdf, navigate, returnPath])

  const handleSubmit = (values: SellContractFormData): void => {
    const formClientId = parseOptionalNumericId(values.client_id)
    if (formClientId === 0) {
      return
    }
    dispatch(
      UPDATE_SELL_CONTRACT({
        id: sellContractId ?? '',
        values: {
          ...sellContractRequestValues(values),
          client_id: formClientId
        }
      })
    )
  }

  const handleCancel = (): void => {
    navigate(returnPath)
  }

  return (
    <Layout withoutToolbar>
      <div className="flex flex-col justify-start items-start gap-4 h-[calc(100vh-100px)]">
        <div className="self-stretch h-14 min-h-14 bg-Estigroup-primary rounded-lg border border-neutral-200 flex flex-col justify-start items-center">
          <div className="self-stretch flex-1 px-4 py-3 inline-flex justify-start items-center">
            <div className="text-foregrounds-fg-on-color text-xl font-semibold font-Montserrat">
              {t('my_vehicles.document_sell_contract')}
            </div>
          </div>
        </div>
        {initialFormValues !== null && (
          <SellContractForm
            mode="edit"
            initialValues={sellContractEditValues(initialFormValues)}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            loading={loading}
          />
        )}
      </div>
    </Layout>
  )
}

export default SellContractUpdate
